import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { content, type Language, type SiteContent } from '../config/siteContent'
import { readConsentChoice } from '../config/consent'
import { alternateLanguagePath, routeFor } from '../config/routes'
import { featuredWorkLinks, siteConfig } from '../config/site'
import { useTheme, type ThemeMode } from '../hooks/useTheme'
import CookieConsent from './CookieConsent'
import { ArrowUpIcon, CloseIcon, MenuIcon, UpworkIcon, ViberIcon } from './icons'

export type SiteContext = {
  language: Language
  copy: SiteContent
}

type MainLayoutProps = { language: Language }

function Brand({ language, footer = false, onNavigate }: { language: Language; footer?: boolean; onNavigate?: () => void }) {
  const copy = content[language]
  return (
    <Link className={`brand ${footer ? 'brand--footer' : ''}`.trim()} to={routeFor(language)} aria-label={siteConfig.brandName} onClick={onNavigate}>
      {siteConfig.assets.logoAvailable ? (
        <img className="brand__logo" src={siteConfig.assets.logoPath} alt="" aria-hidden="true" width="1218" height="1160" />
      ) : (
        <span className="brand__monogram" aria-hidden="true">FM</span>
      )}
      <span className="brand__text">
        <strong>{copy.brand.name}</strong>
        <span>{copy.brand.descriptor}</span>
      </span>
    </Link>
  )
}

function ThemeSwitcher({ language }: { language: Language }) {
  const labels = content[language].theme
  const { mode, setMode } = useTheme()
  const options: Array<{ value: ThemeMode; label: string; icon: string }> = [
    { value: 'auto', label: labels.auto, icon: '◐' },
    { value: 'light', label: labels.light, icon: '☼' },
    { value: 'dark', label: labels.dark, icon: '☾' },
  ]

  return (
    <div className="theme-switch" role="group" aria-label={labels.label}>
      {options.map((option) => (
        <button
          key={option.value}
          className={mode === option.value ? 'is-active' : ''}
          type="button"
          onClick={() => setMode(option.value)}
          aria-pressed={mode === option.value}
          title={option.label}
        >
          <span aria-hidden="true">{option.icon}</span>
          <span className="theme-switch__label">{option.label}</span>
        </button>
      ))}
    </div>
  )
}

export default function MainLayout({ language }: MainLayoutProps) {
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [cookiePanelOpen, setCookiePanelOpen] = useState(false)
  const menuToggleRef = useRef<HTMLButtonElement>(null)
  const copy = content[language]
  const otherLanguage: Language = language === 'en' ? 'el' : 'en'
  const alternatePath = `${alternateLanguagePath(location.pathname, language)}${location.search}${location.hash}`
  const telephoneHref = `tel:${siteConfig.contact.telephone.replace(/[^+\d]/g, '')}`

  useEffect(() => {
    setCookiePanelOpen(readConsentChoice() === null)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
    const frame = window.requestAnimationFrame(() => {
      const targetId = decodeURIComponent(location.hash.slice(1))
      const target = targetId ? document.getElementById(targetId) : null

      if (target) {
        target.scrollIntoView({ block: 'start' })
      } else {
        window.scrollTo({ top: 0, behavior: 'instant' })
      }
    })

    return () => window.cancelAnimationFrame(frame)
  }, [location.hash, location.pathname])

  useEffect(() => {
    if (!menuOpen) return

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.documentElement.classList.add('menu-open')
    document.body.classList.add('menu-open')
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('keydown', closeOnEscape)
      document.documentElement.classList.remove('menu-open')
      document.body.classList.remove('menu-open')
      menuToggleRef.current?.focus({ preventScroll: true })
    }
  }, [menuOpen])

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 1100px)')
    const update = () => setScrolled(!mobileQuery.matches && window.scrollY > 28)
    update()
    window.addEventListener('scroll', update, { passive: true })
    mobileQuery.addEventListener('change', update)
    return () => {
      window.removeEventListener('scroll', update)
      mobileQuery.removeEventListener('change', update)
    }
  }, [])

  return (
    <div className="site-frame">
      <a className="skip-link" href="#main-content">{copy.skipLabel}</a>

      <header className={`site-header ${scrolled ? 'is-compact' : ''}`}>
        <div className="shell site-header__inner">
          <Brand language={language} />

          <nav className="desktop-nav" aria-label={copy.footer.navigation}>
            {copy.nav.map((item) => (
              <NavLink key={item.path} to={routeFor(language, item.path)} end={!item.path} className={({ isActive }) => isActive ? 'is-active' : undefined}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="site-header__actions">
            <ThemeSwitcher language={language} />
            <Link className="language-switch" to={alternatePath} aria-label={`${copy.switchLabel}: ${content[otherLanguage].localeName}`}>
              <span className={language === 'el' ? 'is-active' : ''}>ΕΛ</span><i aria-hidden="true" /><span className={language === 'en' ? 'is-active' : ''}>EN</span>
            </Link>
            <button ref={menuToggleRef} className="menu-toggle" type="button" aria-expanded={menuOpen} aria-controls="mobile-navigation" aria-label={menuOpen ? copy.closeLabel : copy.menuLabel} onClick={() => setMenuOpen((value) => !value)}>
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>

        <div className={`mobile-menu ${menuOpen ? 'is-open' : ''}`} id="mobile-navigation" aria-hidden={!menuOpen} role="dialog" aria-modal="true" aria-label={copy.menuLabel}>
          <div className="mobile-menu__inner">
            <nav className="mobile-menu__nav" aria-label={copy.footer.navigation}>
              {copy.nav.map((item, index) => (
                <NavLink
                  key={item.path}
                  to={routeFor(language, item.path)}
                  end={!item.path}
                  className={({ isActive }) => `mobile-menu__link${isActive ? ' is-active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                  tabIndex={menuOpen ? 0 : -1}
                >
                  <span>0{index + 1}</span>{item.label}
                </NavLink>
              ))}
            </nav>

            <div className="mobile-menu__controls">
              <div className="mobile-menu__theme"><ThemeSwitcher language={language} /></div>
              <div className="mobile-menu__bottom-row">
                <Link className="mobile-menu__language" to={alternatePath} aria-label={`${copy.switchLabel}: ${content[otherLanguage].localeName}`} onClick={() => setMenuOpen(false)} tabIndex={menuOpen ? 0 : -1}>
                  <span className={language === 'el' ? 'is-active' : ''}>ΕΛ</span><i aria-hidden="true" /><span className={language === 'en' ? 'is-active' : ''}>EN</span>
                </Link>
                <a className="mobile-menu__contact" href={telephoneHref} tabIndex={menuOpen ? 0 : -1}>{copy.footer.telephone}</a>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content"><Outlet context={{ language, copy } satisfies SiteContext} /></main>

      <footer className="site-footer">
        <div className="shell site-footer__top">
          <div className="site-footer__identity">
            <Brand language={language} footer />
            <p>{copy.footer.line}</p>
          </div>
          <address className="site-footer__contact">
            <p className="site-footer__heading">{copy.footer.contact}</p>
            <a className="site-footer__contact-link" href={telephoneHref} aria-label={`${copy.footer.telephone}: ${siteConfig.contact.telephone}`}>
              <span>{copy.footer.telephone}</span><strong>{siteConfig.contact.telephone}</strong>
            </a>
            <a className="site-footer__contact-link" href={`mailto:${siteConfig.contact.email}`} aria-label={`Email: ${siteConfig.contact.email}`}>
              <span>Email</span><strong>{siteConfig.contact.email}</strong>
            </a>
            <a className="site-footer__contact-link site-footer__contact-link--viber" href={siteConfig.contact.viberBusiness} aria-label={copy.footer.viber}>
              <span className="site-footer__contact-label"><ViberIcon className="viber-icon" />Viber</span>
              <strong>{copy.footer.viber}</strong>
            </a>
            <a
              className="site-footer__contact-link site-footer__contact-link--upwork"
              href={siteConfig.contact.upwork}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={copy.common.upworkLabel}
            >
              <span className="site-footer__contact-label"><UpworkIcon className="upwork-icon" />Upwork</span>
              <strong>{copy.common.upworkShort}</strong>
            </a>
          </address>
          <nav aria-label={copy.footer.navigation}>
            <p className="site-footer__heading">{copy.footer.navigation}</p>
            {copy.nav.map((item) => <Link key={item.path} to={routeFor(language, item.path)}>{item.label}</Link>)}
            <Link to={routeFor(language, 'privacy')}>{copy.footer.privacy}</Link>
          </nav>
          <nav aria-label={copy.footer.selectedWork}>
            <p className="site-footer__heading">{copy.footer.selectedWork}</p>
            {featuredWorkLinks.map((work) => <a key={work.url} href={work.url} target="_blank" rel="noreferrer">{work.label}</a>)}
          </nav>
        </div>
        <div className="shell site-footer__bottom">
          <div className="site-footer__legal">
            <p>© {new Date().getFullYear()} Fotis Mytikas. {copy.footer.legal}</p>
            <p className="site-footer__credit">{copy.footer.credit}</p>
          </div>
          <div className="site-footer__bottom-actions">
            <button className="site-footer__cookie-settings" type="button" onClick={() => setCookiePanelOpen(true)}>{copy.cookieConsent.settings}</button>
            <Link to={alternatePath}>{content[otherLanguage].localeName}</Link>
            <a href="#top" onClick={(event) => { event.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }}>{copy.footer.top}<ArrowUpIcon /></a>
          </div>
        </div>
      </footer>

      <CookieConsent language={language} open={cookiePanelOpen} onDismiss={() => setCookiePanelOpen(false)} />
    </div>
  )
}
